import { t } from '../../../lib/i18n';
import { monthKeyShortLabel } from '../labels';
import type { RevenueBar } from '../utils';
import { cn, formatNumber } from '../utils';

/**
 * Six stacked bars, one per month — `Redesign-D-Admin`, the «Дохід
 * CarsFinders» card.
 *
 * Stack order bottom-up is retail fee → dealer cut → subscriptions, which is
 * the order of the legend below the chart; reversing either would make the
 * colours stop agreeing between the two.
 *
 * ── The SOLID bar is money that landed ──────────────────────────────────────
 * Each solid segment is that stream's `received` figure for the month. The
 * hatched band above them is everything invoiced in the SAME month and still
 * outstanding — `pending` — and it is drawn separately rather than folded into
 * the stack because the two are different claims. Revenue is recognized at
 * issue, so a late payment moves a figure from the hatched band into the solid
 * one and never between two bars; the bar's height does not change when money
 * arrives, which is exactly what the cap above it prints.
 *
 * Months outside the selected period are drawn at 45% rather than hidden. The
 * scale is fixed by the tallest month in the whole series (see `revenueBars`),
 * so the chart is a constant ruler: switching «Вересень» → «Квартал» dims and
 * undims bars but never moves them.
 *
 * The axis caption comes from the catalogue (`monthKeyShortLabel`), not from
 * the wire: the bucket travels as `2026-09` precisely so that an English
 * operator does not receive «Вер».
 *
 * Segment heights are inline `style` and deliberately so — they are computed
 * pixel values from real data, not design decisions, and a Tailwind arbitrary
 * class per height would generate a class per datum.
 */
export function RevenueBarChart({ bars }: { bars: RevenueBar[] }) {
  return (
    <div className="flex h-[120px] items-end gap-2 border-t border-cf-line-soft pt-1.5 lg:h-[140px] lg:gap-2.5">
      {bars.map((bar) => (
        <div
          key={bar.month}
          className="flex h-full flex-1 flex-col items-stretch justify-end gap-0.5"
        >
          {/* The month total caps the bar on desktop; mobile has no room. */}
          <span className="mb-0.5 hidden text-center font-mono text-[10px] text-cf-ink-subtle lg:block">
            {formatNumber(bar.total)}
          </span>
          {bar.pendingPx > 0 && (
            <div
              className="rounded-t-[3px] border border-dashed border-cf-warn bg-cf-warn-tint"
              style={{ height: `${bar.pendingPx}px` }}
              title={t('adminFinance.revenue.pendingHint')}
            />
          )}
          {bar.forfeitPx > 0 && (
            <div className="bg-cf-info" style={{ height: `${bar.forfeitPx}px` }} />
          )}
          <div className="bg-cf-ink-faint" style={{ height: `${bar.subscriptionPx}px` }} />
          <div className="bg-cf-accent" style={{ height: `${bar.dealerPx}px` }} />
          <div
            className="rounded-b-[3px] bg-cf-brand"
            style={{ height: `${bar.retailPx}px`, opacity: bar.selected ? 1 : 0.45 }}
          />
          <span
            className={cn(
              'mt-1 text-center font-mono text-[10px]',
              bar.selected ? 'text-cf-ink' : 'text-cf-ink-subtle',
            )}
          >
            {monthKeyShortLabel(bar.month)}
          </span>
        </div>
      ))}
    </div>
  );
}
